import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { pinSchema } from '@tailoring-crm/shared';
import { useApiClient } from '../../lib/api/api-context.js';
import { setStaffPin } from '../../lib/auth/auth-api.js';
import { authErrorMessage } from '../../lib/auth/auth-errors.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tailoring-crm/ui';
import { FormError } from './auth-fields.js';
import { PinPad } from './pin-pad.js';

/**
 * An owner or a manager setting a tailor's new PIN.
 *
 * A tailor never resets their own — they have forgotten four digits, they are standing at the
 * counter, and the person who can vouch for them is a metre away. So the flow is: the owner
 * taps the new PIN in on the same pad the tailor will use, says it out loud, and the tailor
 * signs in. No code to an inbox, no reset link to a device that may be at home.
 *
 * The pad is entered twice. A mistyped PIN here does not fail — it succeeds, and locks
 * somebody out of the shop's board until the owner is free again.
 *
 * This component is deliberately self-contained and takes the staff member as props: task 19's
 * staff list drops it in beside each tailor with no changes.
 */
export interface PinResetDialogProps {
  staff: { userId: string; name: string };
  onOpenChange: (open: boolean) => void;
  onDone?: () => void;
}

export function PinResetDialog({ staff, onOpenChange, onDone }: PinResetDialogProps): ReactElement {
  const { t } = useTranslation();
  const client = useApiClient();

  const [pin, setPin] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [stage, setStage] = useState<'enter' | 'confirm'>('enter');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const restart = (message: string): void => {
    setPin('');
    setConfirmation('');
    setStage('enter');
    setError(message);
  };

  const save = (second: string): void => {
    if (second !== pin) {
      restart(t('auth.pinMismatch'));
      return;
    }
    if (!pinSchema.safeParse(pin).success) {
      restart(t('auth.pinInvalid'));
      return;
    }

    setBusy(true);
    setError(null);
    void (async () => {
      try {
        await setStaffPin(client, { userId: staff.userId, pin });
        onDone?.();
        onOpenChange(false);
      } catch (caught) {
        restart(authErrorMessage(caught, t));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('auth.resetPinTitle', { name: staff.name })}</DialogTitle>
          <DialogDescription>
            {stage === 'enter' ? t('auth.resetPinEnter') : t('auth.resetPinConfirm')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <FormError message={error} />

          {stage === 'enter' ? (
            <PinPad
              value={pin}
              onChange={setPin}
              onComplete={() => {
                setStage('confirm');
                setError(null);
              }}
              disabled={busy}
              invalid={error !== null}
              label={t('auth.newPin')}
            />
          ) : (
            <PinPad
              value={confirmation}
              onChange={setConfirmation}
              onComplete={save}
              disabled={busy}
              invalid={error !== null}
              label={t('auth.confirmPin')}
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
